// 導演頁歌頁、時間軸上面的一條小列：這首歌的「歌曲長度」和「起始位置」。
//   歌曲長度：打「分:秒」（3:45 或 225）；有影片或音樂時顯示它們的長度並寫明來源，不能改
//   起始位置：這首歌的第一個 cue 從哪裡走過去（接上一首的結尾 / 指定一個 Cue 庫的 cue / 機關預設位置）
//   第一個 cue 排在 0:00 時，一句白話提示（沒有時間走轉場；不自動改任何 cue 的時間）
// 誰看得到由頁面決定（導演、專案製作、全站製作，Cue 庫專案）；存進後台、可以 Ctrl+Z（useCueOps）。

import { useEffect, useMemo, useRef, useState } from 'react';
import { Flag, Search, Timer, X } from 'lucide-react';
import Icon from '../Icon';
import { listLibraryCues, type LibraryCue } from '../../lib/cueLibraryApi';
import { filterLibraryCues } from '../../lib/cueLibraryList';
import { LENGTH_TEXT, formatLength, parseLengthInput, type SongLengthInfo } from '../../lib/mechanisms/songLength';
import { START_TEXT, type StartMode } from '../../lib/mechanisms/songStart';
import { friendlyError } from '../../lib/songReview';
import type { CueOpsApi } from './useCueOps';
import './SongTimingBar.css';

export interface SongTimingBarProps {
  projectId: string;
  /** 這首歌現在存的 */
  song: { lengthSec?: number | null; startMode?: StartMode; startLibraryCueId?: string | null };
  length: SongLengthInfo;
  /** 起始位置的白話來源（例：接上一首：S02）；沒有 = 不顯示起始位置 */
  startLabel: string | null;
  /** 原本指定的 cue 被刪了（目前退回接上一首） */
  startMissing?: boolean;
  /** 第一個 cue 排在 0:00 */
  firstCueAtZero: boolean;
  /** 能不能改（導演、專案製作、全站製作；預覽別人的畫面時照樣能按，送出時被擋） */
  canEdit: boolean;
  cueOps: Pick<CueOpsApi, 'setLength' | 'setStart' | 'busy'>;
}

export default function SongTimingBar({ projectId, song, length, startLabel, startMissing, firstCueAtZero, canEdit, cueOps }: SongTimingBarProps) {
  const [text, setText] = useState<string | null>(null);
  const [bad, setBad] = useState(false);
  const [dlg, setDlg] = useState(false);
  const shown = length.seconds !== null ? formatLength(length.seconds) : '';
  const editable = canEdit && length.editable;

  const commit = () => {
    if (text === null) return;
    const t = text.trim();
    setText(null);
    if (t === '') { if (song.lengthSec != null) void cueOps.setLength(null, song.lengthSec ?? null); setBad(false); return; }
    const sec = parseLengthInput(t);
    if (sec === null) { setBad(true); return; }
    setBad(false);
    if (sec !== (song.lengthSec ?? null)) void cueOps.setLength(sec, song.lengthSec ?? null);
  };

  return (
    <div className="song-timing-bar">
      <label className="song-timing-bar__field">
        <Icon icon={Timer} />
        <span>{LENGTH_TEXT.label}</span>
        <input
          type="text"
          value={text ?? shown}
          placeholder={LENGTH_TEXT.placeholder}
          aria-label={LENGTH_TEXT.label}
          aria-invalid={bad || undefined}
          disabled={!editable || cueOps.busy}
          title={editable ? LENGTH_TEXT.hint : length.source === 'video' || length.source === 'music' ? LENGTH_TEXT.source(length.source) : LENGTH_TEXT.readonly}
          onFocus={() => setText(shown)}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') { commit(); (e.target as HTMLInputElement).blur(); } if (e.key === 'Escape') { setText(null); setBad(false); (e.target as HTMLInputElement).blur(); } }}
        />
        <em>{LENGTH_TEXT.source(length.source)}</em>
      </label>
      {bad && <span className="song-timing-bar__bad" role="alert">{LENGTH_TEXT.bad}</span>}
      {editable && song.lengthSec != null && (
        <button type="button" className="song-timing-bar__link" onClick={() => { void cueOps.setLength(null, song.lengthSec ?? null); }} disabled={cueOps.busy}>{LENGTH_TEXT.clear}</button>
      )}

      {startLabel !== null && (
        <>
          <span className="song-timing-bar__sep" aria-hidden="true" />
          <button
            type="button"
            className="song-timing-bar__start"
            onClick={() => setDlg(true)}
            disabled={!canEdit || cueOps.busy}
            title={canEdit ? START_TEXT.hint : START_TEXT.readonly}
          >
            <Icon icon={Flag} /> {START_TEXT.button(startLabel)}
          </button>
          {startMissing && <span className="song-timing-bar__note">{START_TEXT.missingNote}</span>}
        </>
      )}
      {firstCueAtZero && startLabel !== null && <span className="song-timing-bar__note">{START_TEXT.moveNote}</span>}
      {dlg && (
        <StartDialog
          projectId={projectId}
          current={{ startMode: song.startMode ?? 'prev', startLibraryCueId: song.startLibraryCueId ?? null }}
          onCancel={() => setDlg(false)}
          onPick={async (body) => {
            setDlg(false);
            await cueOps.setStart(body, { startMode: song.startMode ?? 'prev', startLibraryCueId: song.startLibraryCueId ?? null });
          }}
        />
      )}
    </div>
  );
}

function StartDialog({ projectId, current, onCancel, onPick }: {
  projectId: string;
  current: { startMode: StartMode; startLibraryCueId: string | null };
  onCancel: () => void;
  onPick: (body: { startMode: StartMode; startLibraryCueId?: string | null }) => void;
}) {
  const [mode, setMode] = useState<StartMode>(current.startMode);
  const [pick, setPick] = useState<string | null>(current.startLibraryCueId);
  const [query, setQuery] = useState('');
  const [libCues, setLibCues] = useState<LibraryCue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let off = false;
    listLibraryCues(projectId).then(l => { if (!off) setLibCues(l); }).catch(e => { if (!off) { setLibCues([]); setError(friendlyError(e)); } });
    return () => { off = true; };
  }, [projectId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  useEffect(() => { if (mode === 'library_cue') setTimeout(() => searchRef.current?.focus(), 30); }, [mode]);

  const shown = useMemo(() => filterLibraryCues(libCues ?? [], query, 'all'), [libCues, query]);
  const can = mode !== 'library_cue' || !!pick;
  const options: { key: StartMode; title: string; sub: string }[] = [
    { key: 'prev', title: START_TEXT.prev, sub: START_TEXT.prevSub },
    { key: 'library_cue', title: START_TEXT.library, sub: START_TEXT.librarySub },
    { key: 'defaults', title: START_TEXT.defaults, sub: START_TEXT.defaultsSub },
  ];
  return (
    <div className="dialog-overlay" onClick={onCancel}>
      <div className="dialog add-slot-dlg" role="dialog" aria-modal="true" aria-labelledby="song-start-title" onClick={(e) => e.stopPropagation()}>
        <header className="dialog__header">
          <h2 id="song-start-title">{START_TEXT.title}</h2>
          <button className="dialog__close" onClick={onCancel} title="關閉" aria-label="關閉"><Icon icon={X} size="md" /></button>
        </header>
        <div className="dialog__body">
          <p className="muted small">{START_TEXT.hint}</p>
          <div className="song-start-dlg__opts" role="radiogroup" aria-label={START_TEXT.title}>
            {options.map(o => (
              <button key={o.key} type="button" role="radio" aria-checked={mode === o.key} className={'song-start-dlg__opt' + (mode === o.key ? ' is-selected' : '')} onClick={() => setMode(o.key)}>
                <strong>{o.title}</strong>
                <span className="muted small">{o.sub}</span>
              </button>
            ))}
          </div>
          {mode === 'library_cue' && (
            <>
              <label className="add-slot-dlg__search" style={{ marginTop: 10 }}>
                <Icon icon={Search} />
                <input ref={searchRef} type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={START_TEXT.search} aria-label={START_TEXT.search} />
              </label>
              <div className="add-slot-dlg__list" role="listbox" aria-label={START_TEXT.pickCue}>
                {libCues === null ? <div className="add-slot-dlg__empty muted">載入中…</div>
                  : libCues.length === 0 ? <div className="add-slot-dlg__empty muted">{START_TEXT.noCues}</div>
                  : shown.length === 0 ? <div className="add-slot-dlg__empty muted">沒有符合的 cue</div>
                  : shown.map(lc => (
                    <button key={lc.id} type="button" role="option" aria-selected={pick === lc.id} className={'add-slot-dlg__opt' + (pick === lc.id ? ' is-selected' : '')} onClick={() => setPick(lc.id)}>
                      <span className="slot-badge">{lc.label}</span>
                      <span className="add-slot-dlg__name">{lc.name}</span>
                    </button>
                  ))}
              </div>
            </>
          )}
          {error && <div className="cue-save-dlg__error" role="alert">{error}</div>}
          <div className="dialog__footer">
            <button type="button" className="btn btn--ghost" onClick={onCancel}>取消</button>
            <button type="button" className="btn btn--primary" disabled={!can} onClick={() => onPick(mode === 'library_cue' ? { startMode: mode, startLibraryCueId: pick } : { startMode: mode })}>{START_TEXT.save}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
